/**
 * TestDefinitionListColumn.tsx
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — B1: the master list.
 *
 * Search (name/ASTM/BST/category) + Category select + status filter chips
 * with counts (All / No recipe / Needs machine·time / Unconfirmed / Unmapped /
 * Inactive). Rows: TestName, ASTM code, price (CAD), compact status chips.
 * Problems sort first (testDefinitionModel.filterAndSortDefinitions).
 *
 * Layout mirrors the user-approved Test Mapping master column (D5).
 * Colors: theme tokens only — no hex literals.
 */

import {
  Box, Chip, InputAdornment, List, ListItemButton, MenuItem, TextField,
  Typography, alpha, useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import { SCHEDULE_CATEGORY_OPTIONS } from '../../../constants/scheduleEnums';
import type { TestDefinitionItem } from '../../../services/testDefinitionService';
import { formatCad } from './testCatalogHelpers';
import {
  deriveDefinitionFlags,
  type DefinitionFlags,
  type DefinitionListFilter,
} from './testDefinitionModel';

// ── Status chips ──────────────────────────────────────────────────────────────

interface ChipMeta {
  label: string;
  color: string;
  filled: boolean;
}

/** Compact per-row problem chips, most severe first. */
export function definitionChipMetas(flags: DefinitionFlags, theme: Theme): ChipMeta[] {
  const chips: ChipMeta[] = [];
  if (flags.noRecipe) {
    chips.push({ label: 'No recipe', color: theme.palette.error.main, filled: true });
  }
  if (flags.needsMachineTime) {
    chips.push({ label: 'Needs machine/time', color: theme.schedule.maintenance, filled: true });
  }
  if (flags.unmapped) {
    chips.push({ label: 'Unmapped', color: theme.admin.mappingUnconfirmed, filled: true });
  }
  if (flags.unconfirmedMapping) {
    chips.push({ label: 'Unconfirmed', color: theme.admin.mappingUnconfirmed, filled: false });
  }
  if (flags.inactive) {
    chips.push({ label: 'Inactive', color: theme.admin.mappingNameMatch, filled: false });
  }
  return chips;
}

export const DefinitionStatusChips = ({ flags }: { flags: DefinitionFlags }) => {
  const theme = useTheme();
  const metas = definitionChipMetas(flags, theme);
  return (
    <>
      {metas.map((meta) => (
        <Chip
          key={meta.label}
          size="small"
          label={meta.label}
          sx={{
            height: 18,
            fontSize: '0.6rem',
            fontWeight: 700,
            backgroundColor: alpha(meta.color, meta.filled ? 0.16 : 0.06),
            color: meta.color,
            border: meta.filled ? 'none' : `1px solid ${alpha(meta.color, 0.5)}`,
          }}
        />
      ))}
    </>
  );
};

// ── Filter chips ──────────────────────────────────────────────────────────────

const FILTERS: { key: DefinitionListFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'noRecipe', label: 'No recipe' },
  { key: 'needsMachineTime', label: 'Needs machine·time' },
  { key: 'unconfirmed', label: 'Unconfirmed mapping' },
  { key: 'unmapped', label: 'Unmapped' },
  { key: 'inactive', label: 'Inactive' },
];

// ── Column ────────────────────────────────────────────────────────────────────

interface TestDefinitionListColumnProps {
  /** Already filtered + sorted (testDefinitionModel.filterAndSortDefinitions). */
  items: TestDefinitionItem[];
  counts: Record<DefinitionListFilter, number>;
  search: string;
  onSearchChange: (value: string) => void;
  category: string;
  onCategoryChange: (value: string) => void;
  filter: DefinitionListFilter;
  onFilterChange: (value: DefinitionListFilter) => void;
  selectedKey: string | null;
  onSelect: (catalogKey: string) => void;
  emptyMessage: string;
}

export const TestDefinitionListColumn = ({
  items,
  counts,
  search,
  onSearchChange,
  category,
  onCategoryChange,
  filter,
  onFilterChange,
  selectedKey,
  onSelect,
  emptyMessage,
}: TestDefinitionListColumnProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search name, ASTM, BST, category…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          size="small"
          label="Category"
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">All</MenuItem>
          {SCHEDULE_CATEGORY_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
        {FILTERS.map(({ key, label }) => {
          const active = filter === key;
          return (
            <Chip
              key={key}
              size="small"
              clickable
              label={`${label} (${counts[key]})`}
              onClick={() => onFilterChange(key)}
              sx={{
                fontWeight: active ? 700 : 500,
                fontSize: '0.7rem',
                backgroundColor: active
                  ? alpha(theme.palette.primary.main, 0.14)
                  : 'transparent',
                color: active ? theme.palette.primary.main : theme.palette.text.secondary,
                border: `1px solid ${active ? theme.palette.primary.main : theme.border.color}`,
              }}
            />
          );
        })}
      </Box>

      <List dense disablePadding sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
            {emptyMessage}
          </Typography>
        )}
        {items.map((item) => {
          const flags = deriveDefinitionFlags(item);
          const selected = item.TestCatalogItemKey === selectedKey;
          return (
            <ListItemButton
              key={item.TestCatalogItemKey}
              selected={selected}
              onClick={() => onSelect(item.TestCatalogItemKey)}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                border: `1px solid ${selected ? theme.palette.primary.main : theme.border.color}`,
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: 0.25,
                opacity: flags.inactive ? 0.6 : 1,
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ fontWeight: 600, fontSize: '0.8rem', flex: 1, minWidth: 0 }}
                >
                  {item.TestName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem', flexShrink: 0 }}
                >
                  {formatCad(item.UnitPriceCad)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', flexWrap: 'wrap' }}>
                {item.AstmCode && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, fontSize: '0.65rem' }}
                  >
                    {item.AstmCode}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <DefinitionStatusChips flags={flags} />
              </Box>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};
